import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Navigate } from 'react-router-dom';

import { api } from '../../../core/network/api';
import { broadcastLogout } from '../../../core/network/session';
import { canFromGrants } from '../../../core/authz/match';

// TSS vite/05 — Authentication context. Cookie+CSRF transport.
//
// The JWT lives in an httpOnly cookie set by the backend on /auth/login.
// JS never sees it. csrf_token (the JS-readable companion) is read by
// the api interceptor and echoed in X-CSRF-Token on mutating requests.
//
// This file owns: session state, the permissions capability block, permission
// refresh, route guards, and the <Gate> component.
//
// Permissions shape (api-reference/auth-permissions.md, decisions/0008):
//   { role, superuser, grants: ['animals:*', 'aspirations:read', ...] }
// can(domain, action) replicates the server's wildcard matcher — see
// core/authz/match.js.

const EMPTY_PERMISSIONS = { role: null, superuser: false, grants: [] };
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser]                 = useState(null);
    const [initializing, setInitializing] = useState(true);
    const [permissions, setPermissions]   = useState(EMPTY_PERMISSIONS);

    const refreshTimer = useRef(null);

    const applyPermissions = useCallback((block) => {
        setPermissions({
            role:      block?.role ?? null,
            superuser: !!block?.superuser,
            grants:    Array.isArray(block?.grants) ? block.grants : [],
        });
    }, []);

    const refreshPermissions = useCallback(async () => {
        try {
            const block = await api.get('auth/permissions').then(r => r.data);
            applyPermissions(block);
        } catch {
            // Silent — refresh on 403 is opportunistic.
        }
    }, [applyPermissions]);

    // Bootstrap: /auth/me returns the user + its permissions block in one call.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const me = await api.get('auth/me').then(r => r.data);
                if (cancelled) return;
                const u = me?.user || me;
                setUser(u);
                applyPermissions(u?.permissions);
            } catch {
                if (cancelled) return;
                setUser(null);
            } finally {
                if (!cancelled) setInitializing(false);
            }
        })();
        return () => { cancelled = true; };
    }, [applyPermissions]);

    // Refresh on window focus + on the auth:permissions-refresh signal
    // emitted by the api interceptor when a 403 lands.
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                clearTimeout(refreshTimer.current);
                refreshTimer.current = setTimeout(refreshPermissions, 250);
            }
        };
        const onRefresh = () => refreshPermissions();

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('auth:permissions-refresh', onRefresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('auth:permissions-refresh', onRefresh);
            clearTimeout(refreshTimer.current);
        };
    }, [refreshPermissions]);

    // Multi-tab logout sync — TSS vite/05 §"Logout-everywhere".
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === 'logout_signal') {
                setUser(null);
                setPermissions(EMPTY_PERMISSIONS);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const login = useCallback(async (credentials) => {
        const res = await api.post('auth/login', credentials).then(r => r.data);
        const u = res?.user || res;
        setUser(u);
        applyPermissions(u?.permissions);
    }, [applyPermissions]);

    const logout = useCallback(async () => {
        // Always clear local state, even if the network call fails.
        try { await api.post('auth/logout'); } catch { /* ignore */ }
        setUser(null);
        setPermissions(EMPTY_PERMISSIONS);
        broadcastLogout();
    }, []);

    // can('animals', 'read') or can('animals:read'). Wildcard semantics
    // replicate the server (core/authz/match.js).
    const can = useCallback((domain, action) => {
        return canFromGrants(permissions, domain, action);
    }, [permissions]);

    const canAny = useCallback((...domains) => {
        if (!domains.length) return true;
        return domains.some(d => can(d));
    }, [can]);

    const canAll = useCallback((...domains) => {
        if (!domains.length) return true;
        return domains.every(d => can(d));
    }, [can]);

    const value = useMemo(() => ({
        user,
        isAuthenticated: !!user,
        initializing,
        permissions,
        can, canAny, canAll,
        refreshPermissions,
        login, logout,
    }), [user, initializing, permissions, can, canAny, canAll, refreshPermissions, login, logout]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}

// <Gate> — TSS vite/05 §"UI gating — <Gate> only".
// The ONLY way to conditional-render JSX by permission. Inline can() in
// JSX is forbidden; use can/canAny/canAll only outside JSX.
export function Gate({ domain, action, any, all, fallback = null, children }) {
    const { can, canAny, canAll } = useAuth();

    let allowed = true;
    if (domain) allowed = can(domain, action);
    else if (any && any.length) allowed = canAny(...any);
    else if (all && all.length) allowed = canAll(...all);

    return allowed ? <>{children}</> : <>{fallback}</>;
}

// Route guards — TSS vite/05 §"Route guards".

export function ProtectedRoute({ children, domain, action, any }) {
    const { isAuthenticated, initializing, can, canAny } = useAuth();

    if (initializing) return <BootLoader />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (domain && !can(domain, action)) return <Navigate to="/unauthorized" replace />;
    if (any && any.length && !canAny(...any)) return <Navigate to="/unauthorized" replace />;
    return children;
}

export function AuthRoute({ children }) {
    const { isAuthenticated, initializing } = useAuth();
    if (initializing) return <BootLoader />;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    return children;
}

function BootLoader() {
    return (
        <div className="boot-loader">
            <div className="boot-loader__spinner" />
            <span className="boot-loader__text">Verificando sesión...</span>
        </div>
    );
}
